/**
 * Biometric unlock service (Face ID / Touch ID on iOS, Fingerprint on Android).
 *
 * Web safe: all methods gracefully degrade to "not available" on the browser so
 * the same React code runs in both the Capacitor iOS wrapper and the web preview.
 *
 * Flow:
 *   1. After a user signs in, we call `enableBiometric(token, email)` which stores the
 *      JWT in the OS keychain gated by Face/Touch ID.
 *   2. On subsequent app opens, we call `tryBiometricUnlock()` which prompts Face ID,
 *      and on success returns the stored token so AuthContext can auto-restore the session.
 *   3. The patient can disable biometric unlock from the Profile page (`disableBiometric`).
 *
 * Keys used:
 *   - Keychain/Keystore: service="ElevatingMinds", username=<email>, password=<JWT>
 *   - Capacitor Preferences: "em_biometric_email" (so we know which credential to load)
 */

const KEYCHAIN_SERVICE = "ElevatingMinds";
const BIOMETRIC_EMAIL_KEY = "em_biometric_email";

let _native = null;
let _prefs = null;

async function _loadNative() {
  if (_native !== null) return _native;
  try {
    // Dynamic import so the browser bundle doesn't choke on native-only modules
    const { NativeBiometric, BiometryType } = await import("capacitor-native-biometric");
    const { Capacitor } = await import("@capacitor/core");
    _native = Capacitor?.isNativePlatform?.() ? { NativeBiometric, BiometryType, Capacitor } : false;
  } catch {
    _native = false;
  }
  return _native;
}

async function _loadPrefs() {
  if (_prefs !== null) return _prefs;
  // Only load Preferences on native — on web the plugin throws "not implemented on web"
  const native = await _loadNative();
  if (!native) {
    _prefs = false;
    return _prefs;
  }
  try {
    const { Preferences } = await import("@capacitor/preferences");
    _prefs = Preferences;
  } catch {
    _prefs = false;
  }
  return _prefs;
}

/** True only inside Capacitor (iOS/Android) with biometric hardware + enrollment. */
export async function isBiometricAvailable() {
  const n = await _loadNative();
  if (!n) return { available: false, reason: "web" };
  try {
    const result = await n.NativeBiometric.isAvailable();
    return {
      available: !!result.isAvailable,
      biometryType: result.biometryType, // FACE_ID | TOUCH_ID | FINGERPRINT | etc.
      reason: result.isAvailable ? "" : result.errorCode || "unavailable",
    };
  } catch (e) {
    return { available: false, reason: e?.message || "error" };
  }
}

/** Returns "Face ID" | "Touch ID" | "Biometrics" based on the device. */
export async function getBiometryLabel() {
  const { biometryType } = await isBiometricAvailable();
  if (biometryType === undefined) return "Biometrics";
  const n = await _loadNative();
  const T = n?.BiometryType || {};
  if (biometryType === T.FACE_ID) return "Face ID";
  if (biometryType === T.TOUCH_ID) return "Touch ID";
  if (biometryType === T.FINGERPRINT) return "Fingerprint";
  return "Biometrics";
}

/**
 * Save the current session JWT behind biometric.
 * On iOS, this stores the token in the Keychain with biometric ACL.
 */
export async function enableBiometric(token, email) {
  const n = await _loadNative();
  const prefs = await _loadPrefs();
  if (!n || !token || !email) return { ok: false, reason: "unsupported" };
  try {
    // Remove any prior credential to avoid stale entries for a different user
    try {
      await n.NativeBiometric.deleteCredentials({ server: KEYCHAIN_SERVICE });
    } catch { /* ignore */ }

    await n.NativeBiometric.setCredentials({
      username: email,
      password: token,
      server: KEYCHAIN_SERVICE,
    });
    if (prefs) await prefs.set({ key: BIOMETRIC_EMAIL_KEY, value: email });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e?.message || "save_failed" };
  }
}

/** Forget the stored credential and disable biometric unlock. */
export async function disableBiometric() {
  const n = await _loadNative();
  const prefs = await _loadPrefs();
  if (!n) return { ok: true };
  try {
    await n.NativeBiometric.deleteCredentials({ server: KEYCHAIN_SERVICE });
  } catch { /* ignore — already gone */ }
  if (prefs) await prefs.remove({ key: BIOMETRIC_EMAIL_KEY });
  return { ok: true };
}

/** True if we previously stored a token for some user on this device. */
export async function hasStoredBiometric() {
  const prefs = await _loadPrefs();
  if (!prefs) return false;
  try {
    const { value } = await prefs.get({ key: BIOMETRIC_EMAIL_KEY });
    return !!value;
  } catch {
    return false;
  }
}

/**
 * Prompt the OS biometric dialog; on success, return the stored JWT so the caller can
 * restore the session via AuthContext.checkAuth(). Returns null on any failure or cancel.
 */
export async function tryBiometricUnlock() {
  const n = await _loadNative();
  const prefs = await _loadPrefs();
  if (!n) return null;
  try {
    const stored = prefs ? await prefs.get({ key: BIOMETRIC_EMAIL_KEY }) : { value: null };
    if (!stored?.value) return null;

    await n.NativeBiometric.verifyIdentity({
      reason: "Unlock Elevated Psychiatry",
      title: "Unlock Elevated Psychiatry",
      subtitle: "Use biometrics to access your portal",
      description: "Your session is secured on-device.",
      useFallback: true, // allow iOS passcode fallback
    });

    const creds = await n.NativeBiometric.getCredentials({ server: KEYCHAIN_SERVICE });
    if (creds?.password) {
      return { token: creds.password, email: creds.username || stored.value };
    }
    return null;
  } catch {
    return null;
  }
}
